import { useEffect, useState } from "react";

import { Box, Button, Typography } from "@mui/material";

import DbAutocomplete from "../../inputs/autocomplete/DbAutocomplete";
import AddOfficesForm from "../../inputs/offices/AddOfficesForm";
import AddVehiclesInputs from "../../inputs/vehicles/AddVehiclesInputs";

import { useCachedDisplayData } from "../../../helpers/firebase";

const buildOfficeOptions = (offices) =>
  offices.map(({ id, name, endDate }) => ({ id, name, isClosed: !!endDate }));

const buildVehicleOptions = (vehicles) =>
  vehicles.map(({ id, name }) => ({ id, name }));

const entityLabelByType = { offices: "office", vehicles: "vehicle" };

// resolveAllocation()'s candidates carry `label` (an office's raw
// `address.description`, or a vehicle's `name`) and `entityId` -- not the
// picker's own `{id, name}` option shape. Looking each candidate up in the
// already-fetched full list keeps the picker showing the SAME name the rest
// of the app shows for that office/vehicle, rather than a raw address
// string standing in for it (self-review finding: "backend labels by
// address.description, picker shows name -- mismatch"). Falls back to the
// backend's own label only if the entity has since disappeared from the
// full list (a narrow, honest degradation, not the common case).
const candidateOptions = (candidates, fullOptions) =>
  candidates.map(({ entityId, label }) => {
    const known = fullOptions.find(({ id }) => id === entityId);
    return known || { id: entityId, name: label };
  });

const matchSourceCopy = {
  context: "Uploaded directly to this entity — confirm this is correct.",
  learned:
    "Matched to a previously confirmed meter/address mapping — confirm this is correct.",
  matched: "Possible match found — confirm it, or pick a different one below."
};

// Standalone, composable per-line allocation UI (spec: ticket
// 9zFp7uWgIg1zwzgQT8ia, New Files > AllocationStep.js). Not wired into
// BomReviewTable.js/BomConfirmationScreen.js in this PR -- exported for the
// "Review, confirm & per-row commit" ticket (mvXzSh6tWRLWovWhfQ4F) to
// compose once that generalized review surface exists.
//
// Deliberately not a reuse of OfficeInput.js/VehicleInput.js: those exclude
// closed offices and include personal/employee-nested vehicles, both wrong
// for allocation (Edge Cases -- "a closed office must remain a valid
// allocation target," "company vehicles only").
//
// `resolution` is resolveAllocation()'s own output shape. `null` means this
// line has no physical location at all (a null allocationSignal with no
// entity_context, or an explicit vendor-relationship entity_context --
// resolveAllocation already collapses both cases to null) -- render nothing,
// not even an empty picker (design-intent 3.2).
//
// `lineId` is a stable identifier for the row this instance represents
// (e.g. a taskData row id) -- combined with `resolution.entityId`/
// `matchSource` (not the resolution object's own identity) as the reset
// trigger below. Each piece alone was wrong on its own: `lineId` alone never
// re-fires if `resolution` arrives asynchronously or gets re-resolved for
// the SAME line (a context/learned match would stay stuck on the initial
// null pre-fill, Confirm permanently disabled); `resolution`'s object
// identity alone wiped an in-progress pick whenever a parent recomputed a
// fresh resolution object for the same line (a live Firestore listener
// recomputing lines[] on every snapshot, the realistic shape of the future
// Review/confirm/commit consumer). Depending on all three together resets
// exactly when the line changes OR the resolved values actually change,
// and never when the same values are simply recomputed as a new object
// (self-review finding, round 2). Until a real caller passes a distinct
// `lineId` per line, this degrades to the entityId/matchSource-only
// behavior -- a known, accepted gap for a component with no real caller yet.
const AllocationStep = ({ entityType, resolution, onConfirm, lineId }) => {
  const [offices] = useCachedDisplayData("offices", []);
  const [vehicles] = useCachedDisplayData("vehicles", [
    ["type", "==", "company"]
  ]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState(
    resolution?.entityId || null
  );

  useEffect(() => {
    setSelectedEntityId(resolution?.entityId || null);
    setShowCreateForm(false);
  }, [lineId, resolution?.entityId, resolution?.matchSource]);

  if (!resolution) {
    return null;
  }

  const options =
    entityType === "offices"
      ? buildOfficeOptions(offices)
      : buildVehicleOptions(vehicles);
  const entityLabel = entityLabelByType[entityType];
  // Derived from `resolution` alone, never from the user's own selection --
  // picking a candidate must not make the "this was ambiguous" state (and
  // the narrowed candidate list below) disappear mid-review (self-review
  // finding: the previous `!selectedEntityId && ...` version silently
  // expanded back to the full company list, and dropped the pre-filled
  // fallback-label candidate, the instant the reviewer picked one).
  const isAmbiguous = !resolution.entityId && resolution.candidates?.length > 1;
  // Never silently pick between an ambiguous set (Edge Cases) -- narrow the
  // picker to just the candidates resolveAllocation actually found, rather
  // than the full company list, so "present both as choices" is what
  // actually renders.
  const displayOptions = isAmbiguous
    ? candidateOptions(resolution.candidates, options)
    : options;
  const selectedOption =
    displayOptions.find(({ id }) => id === selectedEntityId) || null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {matchSourceCopy[resolution.matchSource] && (
        <Typography variant="body2" color="textSecondary">
          {matchSourceCopy[resolution.matchSource]}
        </Typography>
      )}
      {isAmbiguous && (
        <Typography variant="caption" color="textSecondary">
          Multiple possible matches — pick the correct one below.
        </Typography>
      )}
      <DbAutocomplete
        availableOptions={displayOptions}
        value={selectedOption}
        setValue={(newValue) => setSelectedEntityId(newValue?.id || null)}
        label={`Select a ${entityLabel}`}
        id={`allocation-step-${entityType}-input`}
      />
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button size="small" onClick={() => setShowCreateForm(true)}>
          {`Create a new ${entityLabel}`}
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={!selectedEntityId}
          onClick={() => onConfirm({ id: selectedEntityId, entityType })}
        >
          Confirm
        </Button>
      </Box>
      {showCreateForm &&
        (entityType === "offices" ? (
          <AddOfficesForm
            setOpen={setShowCreateForm}
            onFormSubmit={() => setShowCreateForm(false)}
            isSliderLeftContent
          />
        ) : (
          <AddVehiclesInputs
            setOpen={setShowCreateForm}
            onFormSubmit={() => setShowCreateForm(false)}
            isSliderLeftContent
          />
        ))}
    </Box>
  );
};
export default AllocationStep;
